/**
 * Maps a team name to a hue color hex value.
 * Used for the mouse-tracking glow effect on cards.
 */

const hueColors: string[] = [
  "#E11837", // red
  "#3B82F6", // blue
  "#22C55E", // green
  "#A855F7", // purple
  "#F59E0B", // amber
  "#06B6D4", // cyan
  "#EC4899", // pink
  "#F97316", // orange
];

// Known team name → color mappings
const knownTeams: Record<string, string> = {
  engineering: "#E11837",
  eng: "#E11837",
  residential: "#3B82F6",
  golf: "#22C55E",
  commercial: "#F59E0B",
  design: "#A855F7",
  product: "#06B6D4",
  marketing: "#EC4899",
  operations: "#F97316",
};

// Known team name → Fluent-style SVG icon path data (20x20 viewBox)
const knownTeamIcons: Record<string, string> = {
  residential:
    // House icon
    "M10 2.5l-7.5 6v9a1 1 0 001 1h4.5v-5a1 1 0 011-1h2a1 1 0 011 1v5h4.5a1 1 0 001-1v-9l-7.5-6z",
  golf:
    // Flag on green
    "M6 2v16M6 2l8 4-8 4M3 17a1 1 0 011-1h4a1 1 0 011 1v0a1 1 0 01-1 1H4a1 1 0 01-1-1v0z",
  engineering:
    // Wrench/gear icon
    "M14.7 6.3a1 1 0 000 1.4l-1.6 1.6a4 4 0 01-5.4 5.4L6.1 16.3a1 1 0 01-1.4 0l-1-1a1 1 0 010-1.4l1.6-1.6a4 4 0 015.4-5.4l1.6-1.6a1 1 0 011.4 0l1 1zM10 8.5a2 2 0 100 4 2 2 0 000-4z",
  eng:
    "M14.7 6.3a1 1 0 000 1.4l-1.6 1.6a4 4 0 01-5.4 5.4L6.1 16.3a1 1 0 01-1.4 0l-1-1a1 1 0 010-1.4l1.6-1.6a4 4 0 015.4-5.4l1.6-1.6a1 1 0 011.4 0l1 1zM10 8.5a2 2 0 100 4 2 2 0 000-4z",
  commercial:
    // Building icon
    "M4 3a1 1 0 011-1h10a1 1 0 011 1v15H4V3zm3 2h2v2H7V5zm4 0h2v2h-2V5zM7 9h2v2H7V9zm4 0h2v2h-2V9zM8 14h4v4H8v-4z",
  design:
    // Pen icon
    "M15.5 4.5l-11 11L3 18l2.5-1.5 11-11-1-1zM13 6l1 1",
  product:
    // Box icon
    "M10 2L3 6v8l7 4 7-4V6l-7-4zM3 6l7 4m0 0v8m0-8l7-4",
  marketing:
    // Megaphone icon
    "M3 10v4a1 1 0 001 1h1l2 3h2l-2-3h2l6 3V4L9 7H4a1 1 0 00-1 1v2z",
  operations:
    // Gear icon
    "M10 7a3 3 0 100 6 3 3 0 000-6zM10 2v2m0 12v2m8-8h-2M4 10H2m14.1-5.1l-1.4 1.4M5.3 14.7l-1.4 1.4m12.2 0l-1.4-1.4M5.3 5.3L3.9 3.9",
};

// Default icon (users group)
const DEFAULT_ICON = "M7 8a3 3 0 116 0 3 3 0 01-6 0zM3 17a5 5 0 0114 0v1H3v-1z";

function simpleHash(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

export function getTeamHueColor(teamName: string | undefined | null): string {
  if (!teamName) return "#3B82F6";
  
  const lower = teamName.toLowerCase();
  
  const known = knownTeams[lower];
  if (known) return known;
  
  const index = simpleHash(lower) % hueColors.length;
  return hueColors[index] ?? "#3B82F6";
}

/** Returns an SVG path data string for the team icon */
export function getTeamIconPath(teamName: string | undefined | null): string {
  if (!teamName) return DEFAULT_ICON;
  const lower = teamName.toLowerCase();
  return knownTeamIcons[lower] ?? DEFAULT_ICON;
}
